// Page-specific tagging for the inherited Stellantis "Uconnect" SiteBuilder page.
// Alias: SITEBUILDER_UCONNECT_INTELLIGENT_VEHICLE_CONNECTIVITY_1 (US2021127)
// Spec: "Stellantis Adobe Analytics - Uconnect | Dealer Site Page".
//
// Payloads mirror the client's digitalData.newEvent(...) shape; Stellantis' Adobe
// Launch property flattens the attributes (site/type/location/title/description/...)
// into eVar75 / eVar184 / eVar172 / eVar174 and the events (event70/178/183).
//
// Rules are unshifted so they win over the generic stellantis ddc.link.generic
// rules (the mapper uses first-match), which is what keeps a disclaimer click from
// also firing the old CTA taxonomy.

// Format component text the way the client does (lowercase, spaces -> hyphens).
// Idempotent: if Launch reformats it again, nothing changes.
const slug = (str = '') =>
	String(str).toLowerCase().trim().replace(/\s+/g, '-');

// Strip Dealer.com's click-tracking param (_cl=...) that DDC appends to outbound
// links at click time, so linkUrl (eVar174) is the clean store URL. Only removes
// _cl; any other query params (e.g. Google Play's id/hl) are preserved verbatim.
const stripCl = (url = '') =>
	url
		.replace(/([?&])_cl=[^&#]*(&)?/i, (_match, sep, amp) => {
			if (sep === '?') {
				return amp ? '?' : '';
			}
			return amp ? sep : '';
		})
		.replace(/[?&]$/, '');

// Which feature component (position + title) each disclaimer footnote sits in,
// taken from the spec's own examples for this page.
const DISCLAIMER_FEATURE = {
	1: { location: 1, title: 'intelligent-vehicle-connectivity' },
	2: { location: 1, title: 'intelligent-vehicle-connectivity' },
	3: { location: 2, title: 'enjoy-10-years-of-connect-one-on-us!' }
};

// disclaimer index parsed from an href like "#disclaimer3"
const disclaimerNum = (url = '') => {
	const match = /#disclaimer(\d+)/.exec(url);
	return match ? Number(match[1]) : undefined;
};

// Scratch space this widget owns (same convention as utils/swipingDirection.js).
// We remember which disclaimer opened so the later ddc.dialog.closed event can
// report the matching overlay location.
const rememberDisclaimer = (num) => {
	const wsTagging = window?.DDC?.WidgetData?.['ws-tagging'];
	if (wsTagging) {
		wsTagging.uconnectDisclaimer = num;
	}
};

// DDC video signal -> the spec's assetAction values.
// Confirmed against real events on nonprod (US2061191): ws-tracking sends
// videoMilestone as strings ('25'/'50'/'75'/'complete'), which still hit these
// keys since object keys are strings either way. It ALSO sends a '95' milestone
// that the spec has no assetAction for -- see the milestone rule below.
const ASSET_ACTION = {
	25: '25%-view',
	50: '50%-view',
	75: '75%-view',
	complete: 'complete'
};

// Only the milestones above are in the client's assetAction vocabulary.
const isSupportedMilestone = (videoMilestone) =>
	Object.prototype.hasOwnProperty.call(ASSET_ACTION, videoMilestone);

// title is the feature component the video sits in — same location-2 component as
// disclaimer 3 above. Missing from the original spec; added per US2061191.
const mediaPayload = (assetAction) => ({
	type: 'CustomTagEvent',
	eventName: 'media',
	eventAction: 'interaction',
	attributes: {
		site: 'dealer',
		type: 'feature',
		title: 'enjoy-10-years-of-connect-one-on-us!',
		location: 2,
		description: assetAction,
		assetType: 'video',
		assetSource: 'dealership',
		assetName: 'uconnect.mp4'
	}
});

export default {
	events: {
		'ddc.link.generic': [
			// Disclaimer opening clicks -> feature interaction (description = footnote number)
			{
				insertion: 'unshift',
				logic: ({ eventData: { url = '' } = {} }) =>
					url.startsWith('#disclaimer'),
				payload: ({ eventData: { url = '', text = '' } = {} }) => {
					const num = disclaimerNum(url);
					rememberDisclaimer(num);
					const { location, title } = DISCLAIMER_FEATURE[num] || {};
					return {
						type: 'CustomTagEvent',
						eventName: 'linkClick',
						eventAction: 'linkClick',
						attributes: {
							site: 'dealer',
							type: 'feature',
							location,
							description: slug(text),
							title
						}
					};
				}
			},
			// Download app clicks -> feature interaction w/ external link (linkUrl -> eVar174)
			{
				insertion: 'unshift',
				// Scoped to content-region links-list widgets only. The Uconnect
				// template's footer is built from five links-list widgets
				// (template-footer-links1..4 + template-links5) that report
				// location:'footer'; those must NOT get type:feature — they fall
				// through to the default footer rule instead (TA4168348).
				logic: ({ eventData: { widgetName, location } = {} } = {}) =>
					widgetName === 'links-list' && location === 'content',
				payload: ({ eventData = {} }) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'feature',
						location: 3,
						description: slug(eventData.text || ''),
						title: 'convenience-at-your-fingertips',
						linkUrl: stripCl(eventData.url || '')
					}
				})
			}
		],
		// Overlay close click -> overlay interaction (location = which disclaimer opened)
		'ddc.dialog.closed': [
			{
				// Prepend so this page-specific overlay close wins over the
				// flag-gated general ddc.dialog.closed rule in default.js.
				insertion: 'unshift',
				payload: () => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'overlay',
						location:
							window?.DDC?.WidgetData?.['ws-tagging']
								?.uconnectDisclaimer,
						description: 'close',
						title: 'n/a'
					}
				})
			}
		],
		// Video interaction: play
		'ddc.video.play': [
			{
				logic: [{ mustNotEqual: { 'eventData.autoplay': true } }],
				payload: mediaPayload('play')
			}
		],
		// Video interaction: resume (play after pause) -> also the spec's "play".
		// ws-tracking fires ddc.video.play only on the FIRST play and
		// ddc.video.resume on every subsequent play (US2044436); mapping both to
		// "play" keeps Stellantis' play/pause/play sequence correct without changing
		// the play-fires-once behavior other tagging projects rely on.
		'ddc.video.resume': [
			{
				payload: mediaPayload('play')
			}
		],
		// Video interaction: milestones (25%/50%/75%/complete).
		// ws-tracking also emits a 95% milestone, which has no assetAction in the
		// client's spec. Unsupported milestones are dropped rather than passed
		// through verbatim, so Adobe never receives an out-of-vocabulary
		// assetAction (a raw '95' otherwise landed on every completed view).
		'ddc.video.milestone': [
			{
				logic: ({ eventData: { videoMilestone } = {} }) =>
					isSupportedMilestone(videoMilestone),
				payload: ({ eventData: { videoMilestone } = {} }) =>
					mediaPayload(ASSET_ACTION[videoMilestone])
			}
		],
		// Video interaction: pause -> spec's "stop" assetAction.
		// ws-tracking emits ddc.video.pause on native <video> pause (US2044436).
		'ddc.video.pause': [
			{
				payload: mediaPayload('stop')
			}
		]
	}
};
